// Pure density-array interpolation for the KDE morph tween.
//
// The GSAP wrapper in kde-chart.ts calls into this on every onUpdate
// frame; everything that has to do with deciding what to render at a
// given `t` lives here, behind a pure boundary that's testable without
// gsap, a canvas, or a hook.

import { scaleLinear } from "d3-scale";

/**
 * Linearly resample a (srcGrid, srcVals) series onto `tgtGrid`.
 *
 * Pure copy of the helpers.ts `kdeResample` body — duplicated here so
 * the test suite can exercise the morph machinery without pulling in
 * helpers.ts's .dox import chain. helpers.ts re-exports from this
 * module via the no-cycle path `helpers → kde-morph`.
 *
 * Precondition: `srcGrid` is monotonically ascending and has length >= 2
 * (matches helpers.kdeResample). Fast-path when grids are same length.
 */
export const kdeResample = (
  srcGrid: ReadonlyArray<number>,
  srcVals: ReadonlyArray<number>,
  tgtGrid: ReadonlyArray<number>,
): number[] => {
  if (srcGrid.length === tgtGrid.length) return srcVals.slice();
  if (srcGrid.length < 2) return tgtGrid.map(() => srcVals[0] ?? 0);
  const interp = scaleLinear<number, number>()
    .domain(srcGrid as number[])
    .range(srcVals as number[])
    .clamp(true);
  return tgtGrid.map((x) => interp(x));
};

/**
 * Pointwise lerp between two density arrays. If lengths differ, the
 * source is first resampled onto the destination's index space so the
 * morph is sample-aligned. NaN-safe (never propagates a NaN — falls
 * back to the destination value on either side being NaN).
 *
 * t clamps to [0, 1]; out-of-range t is treated as 0 / 1 respectively.
 *
 * Invariants tested in test/kde-morph.test.ts:
 *   output.length === newArr.length
 *   t === 0 → output ≈ resampled oldArr (mod NaN-fallback)
 *   t === 1 → output ≈ newArr
 */
export const morphDensities = (
  oldArr: ReadonlyArray<number>,
  newArr: ReadonlyArray<number>,
  t: number,
): number[] => {
  if (newArr.length === 0) return [];
  const u = t <= 0 ? 0 : t >= 1 ? 1 : t;
  // Match the original onUpdate: resample old → new grid via the
  // identity-grid kdeResample fast-path when lengths match; otherwise
  // synthesise unit grids so resample interpolates along index.
  const aligned =
    oldArr.length === newArr.length
      ? oldArr.slice()
      : oldArr.length === 0
        ? new Array<number>(newArr.length).fill(0)
        : kdeResample(
            Array.from({ length: oldArr.length }, (_, i) => i),
            oldArr as number[],
            Array.from({ length: newArr.length }, (_, i) =>
              newArr.length === 1
                ? 0
                : (i * (oldArr.length - 1)) / (newArr.length - 1),
            ),
          );
  // Exact endpoints: the algebraic `od + (nv - od) * u` accumulates a
  // floating-point cancellation error of ~1e-10 at u=1 (subtract then
  // multiply by 1), which violates the `toBeCloseTo(_, 10)` invariant
  // the property test pins. Short-circuit u=1 to the destination and
  // u=0 to the source so the endpoints are bit-exact regardless of
  // input magnitudes.
  if (u === 1) {
    // Both branches collapse to `nv` — the comparison is redundant after
    // TS narrows. Kept the structure aligned with the u=0 branch below
    // so a future maintainer reading the symmetry doesn't have to
    // squint, but lint is correct that the ternary is dead code today.
    return newArr.map((nv) => nv);
  }
  if (u === 0) {
    return aligned.map((od, i) => {
      const nv = newArr[i] ?? 0;
      return Number.isFinite(od) && Number.isFinite(nv) ? od : nv;
    });
  }
  return aligned.map((od, i) => {
    const nv = newArr[i] ?? 0;
    if (!Number.isFinite(od) || !Number.isFinite(nv)) return nv;
    return od + (nv - od) * u;
  });
};
